import { Link, useParams } from "react-router-dom";
import { PageHeader } from "@/shared/components/PageHeader";
import { Button } from "@/shared/components/ui/button";
import { StatusText } from "@/shared/components/ui/status-chip";
import { EmptyState } from "@/shared/components/ui/empty-state";
import { useTaskDetail, useCompleteTask } from "./use-tasks";

const fmt = (iso: string) => new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });

function Field({ label, value }: { label: string; value?: string | number | null }) {
  if (value === undefined || value === null || value === "") return null;
  return (
    <div className="flex flex-col gap-[2px]">
      <span className="ge-eyebrow">{label}</span>
      <span className="font-sans text-[13px] text-ge-primary">{value}</span>
    </div>
  );
}

/** Task detail + the lead needed to work it (read-only). Opens from the tasks list. */
export function TaskDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { data, isLoading, isError, refetch } = useTaskDetail(id);
  const complete = useCompleteTask();

  if (isError) {
    return (
      <div>
        <PageHeader title="Task" />
        <EmptyState
          message="Couldn't load this task."
          action={
            <Button variant="outline" onClick={() => void refetch()}>
              Retry
            </Button>
          }
        />
      </div>
    );
  }
  if (isLoading || !data) {
    return (
      <div>
        <PageHeader title="Task" />
        <div className="text-[13px] text-ge-secondary">Loading…</div>
      </div>
    );
  }

  const { task, lead } = data;
  const overdue = task.status === "PENDING" && new Date(task.dueAt).getTime() < Date.now();

  return (
    <div>
      <PageHeader title={task.title} />
      <Link to="/tasks" className="font-mono text-[11.5px] text-ge-secondary hover:text-ge-accent">
        ← Back to tasks
      </Link>

      <div className="mt-4 grid gap-6 md:grid-cols-2">
        {/* Task */}
        <section className="rounded-ge-card border border-ge-border-subtle p-4" data-testid="task-panel">
          <div className="mb-3 flex items-center justify-between">
            <p className="ge-eyebrow">TASK</p>
            <StatusText tone={task.status === "COMPLETED" ? "accent" : "faint"}>{task.status}</StatusText>
          </div>
          <div className="flex flex-col gap-3">
            {task.instructions ? (
              <div className="flex flex-col gap-[2px]">
                <span className="ge-eyebrow">Instructions</span>
                <span className="whitespace-pre-wrap font-sans text-[13px] text-ge-primary">{task.instructions}</span>
              </div>
            ) : null}
            <div className={overdue ? "font-mono text-[12px] font-semibold text-red-600" : "font-mono text-[12px] text-ge-value"}>
              Due {fmt(task.dueAt)}{overdue ? " · overdue" : ""}
            </div>
            <Field label="Type" value={task.isBlocker ? "Blocking (pauses the sequence)" : "Non-blocking"} />
            {task.status === "PENDING" ? (
              <div>
                <Button data-testid="task-detail-complete" disabled={complete.isPending} onClick={() => complete.mutate(task.id)}>
                  Mark complete
                </Button>
              </div>
            ) : null}
          </div>
        </section>

        {/* Lead */}
        <section className="rounded-ge-card border border-ge-border-subtle p-4" data-testid="lead-panel">
          <p className="ge-eyebrow mb-3">LEAD</p>
          {lead ? (
            <div className="flex flex-col gap-3">
              <Field label="Name" value={lead.name} />
              <Field label="City" value={lead.city?.name} />
              <Field label="Website" value={lead.website} />
              <Field label="Address" value={lead.address} />
              <Field label="Rating" value={lead.rating ?? undefined} />
              <Field label="Status" value={lead.status} />
              {lead.contacts && lead.contacts.length > 0 ? (
                <div className="flex flex-col gap-[2px]">
                  <span className="ge-eyebrow">Contacts</span>
                  {lead.contacts.map((c) => (
                    <span key={c.id} className="font-sans text-[13px] text-ge-primary">
                      {[c.name, c.role, c.email, c.phone].filter(Boolean).join(" · ")}
                    </span>
                  ))}
                </div>
              ) : null}
            </div>
          ) : (
            <div className="text-[13px] text-ge-secondary">Lead not available.</div>
          )}
        </section>
      </div>
    </div>
  );
}
